import { Plus, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { CompactToggle } from '@/components/ui/toggle-setting'
import { formatPercent } from '@/lib/utils'
import PlanCardShell from '@/components/plan/PlanCardShell'
import PlanFieldDelta from '@/components/plan/PlanFieldDelta'
import {
  GroupQuickAddButton,
  GroupRowHeader,
} from '@/components/plan/PlanGroupEditorPrimitives'

function getMaxPossibleMatch(matchTiers = []) {
  return matchTiers.reduce((sum, tier, i) => {
    const prevCeiling = i > 0 ? matchTiers[i - 1].upToPercent : 0
    const tierWidth = Math.max(0, tier.upToPercent - prevCeiling)
    return sum + (tierWidth * tier.matchPercent / 100)
  }, 0)
}

export function createMatchGroup(groupId = '') {
  return {
    id: `match-group-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    groupId,
    matchTiers: [{ matchPercent: 50, upToPercent: 4 }],
  }
}

function MatchTierRow({ tierIndex, tier, idPrefix, labelPrefix, onUpdate, onDelete }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
      <span className="w-12 shrink-0 text-muted-foreground">Tier {tierIndex + 1}</span>
      <span className="flex items-center gap-2 whitespace-nowrap">
        <NumberInput validationId={`${idPrefix}-match`} validationLabel={`${labelPrefix} match %`} aria-label={`${labelPrefix} match percentage`} value={tier.matchPercent} onValueChange={(value) => onUpdate({ matchPercent: value })} containerClassName="w-20" className="w-20" min={0} max={400} step="0.25" />
        <span className="text-muted-foreground">% match up to</span>
      </span>
      <span className="flex items-center gap-2 whitespace-nowrap">
        <NumberInput validationId={`${idPrefix}-up-to`} validationLabel={`${labelPrefix} up-to %`} aria-label={`${labelPrefix} up-to percentage`} value={tier.upToPercent} onValueChange={(value) => onUpdate({ upToPercent: value })} containerClassName="w-20" className="w-20" min={0} max={100} step="0.25" />
        <span className="text-muted-foreground">% of pay deferred</span>
      </span>
      {onDelete && (
        <Button type="button" aria-label={`Delete ${labelPrefix}`} variant="ghost" size="sm" onClick={onDelete} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
      )}
    </div>
  )
}

function MatchGroupRow({ id, index, group, options, employeeCount, displayName, onChange, onDelete }) {
  const tiers = Array.isArray(group.matchTiers) && group.matchTiers.length
    ? group.matchTiers
    : [{ matchPercent: 50, upToPercent: 4 }]
  const handleGroupChange = (value) => {
    onChange({ ...group, groupId: value, matchTiers: tiers })
  }
  const updateTier = (tierIndex, updates) => onChange({
    ...group,
    matchTiers: tiers.map((tier, i) => (i === tierIndex ? { ...tier, ...updates } : tier)),
  })
  const addTier = () => {
    const last = tiers[tiers.length - 1]
    onChange({
      ...group,
      matchTiers: [...tiers, { matchPercent: 25, upToPercent: (last?.upToPercent || 0) + 2 }],
    })
  }

  return (
    <div className="space-y-2.5 rounded-md border bg-background p-3">
      <GroupRowHeader
        ariaLabel={`Match group ${index + 1}`}
        value={group.groupId}
        options={options}
        onValueChange={handleGroupChange}
        employeeCount={employeeCount}
        removeLabel={`Remove ${displayName} match group`}
        onRemove={() => onDelete(index)}
      />

      <div className="space-y-2">
        {tiers.map((tier, tierIndex) => (
          <MatchTierRow
            key={tierIndex}
            tierIndex={tierIndex}
            tier={tier}
            idPrefix={`${id}-${index}-${tierIndex}`}
            labelPrefix={`${displayName} tier ${tierIndex + 1}`}
            onUpdate={(updates) => updateTier(tierIndex, updates)}
            onDelete={tiers.length > 1 ? () => onChange({ ...group, matchTiers: tiers.filter((_, i) => i !== tierIndex) }) : undefined}
          />
        ))}
        <Button type="button" variant="outline" size="sm" className="h-7" onClick={addTier}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add tier
        </Button>
      </div>
    </div>
  )
}

function MatchGroupsEditor({ id, groups, allocationGroups, memberCounts, onAdd, onUpdate, onDelete }) {
  const assignedIds = new Set(groups.map((group) => group.groupId).filter(Boolean))
  const unassignedGroups = allocationGroups.filter((group) => !assignedIds.has(group.id))

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Class-based match formulas use the allocation classes defined on the Employees step.
      </p>
      {groups.length === 0 ? (
        <div className="rounded-md border border-dashed bg-background px-3 py-4 text-sm text-muted-foreground">
          No allocation classes have a separate match formula.
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((group, index) => {
            const allocationGroup = allocationGroups.find((candidate) => candidate.id === group.groupId)
            const options = allocationGroups
              .filter((candidate) => !assignedIds.has(candidate.id) || candidate.id === group.groupId)
              .map((candidate) => ({
                value: candidate.id,
                label: `${candidate.label} · ${memberCounts[candidate.id] || 0}`,
              }))
            return (
              <MatchGroupRow
                key={group.id || index}
                id={id}
                index={index}
                group={group}
                options={options}
                employeeCount={memberCounts[group.groupId] || 0}
                displayName={allocationGroup?.label || 'Unknown group'}
                onChange={(next) => onUpdate(index, next)}
                onDelete={onDelete}
              />
            )
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {unassignedGroups.map((group) => (
          <GroupQuickAddButton key={group.id} label={group.label} count={memberCounts[group.id] || 0} onClick={() => onAdd(group.id)} />
        ))}
      </div>
    </div>
  )
}

// Tiered match, as its own top-level plan card. Previously one of three blocks
// stacked inside an "Employer contributions" wrapper, which added a level of
// nesting and a parallel sub-nav for no benefit: each source is independently
// toggled and independently read.
export default function MatchCard({
  id,
  title,
  description,
  contributions,
  reference,
  compareTo,
  allocationGroups = [],
  matchMemberCounts = {},
  onMatchEnabledChange,
  onMatchModeChange,
  onAddTier,
  onUpdateTier,
  onDeleteTier,
  onResetTiers,
  onAddMatchGroup,
  onUpdateMatchGroup,
  onDeleteMatchGroup,
  onMatchCapChange,
  disabled,
  variant,
}) {
  const {
    matchEnabled,
    matchMode = 'simple',
    matchTiers,
    matchGroups = [],
    matchCapPerEmployee = 0,
  } = contributions
  const maxPossibleMatch = getMaxPossibleMatch(matchTiers)

  return (
    <PlanCardShell
      id={id}
      disabled={disabled}
      variant={variant}
      title={title}
      description={description}
      titleAction={() => onMatchEnabledChange(!matchEnabled)}
      titleActionLabel="Toggle tiered match"
      titleActionDisabled={disabled}
      headerAction={<CompactToggle label="Include tiered match in plan" checked={matchEnabled} onCheckedChange={onMatchEnabledChange} disabled={disabled} />}
      collapsible={false}
    >
      {(reference || matchEnabled) && <CardContent className="space-y-2">
        {reference}
        {matchEnabled && (
          <div className="space-y-2 rounded-md bg-muted/30 p-2.5">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <Label>Mode</Label>
              <Select value={matchMode} onValueChange={onMatchModeChange}>
                <SelectTrigger aria-label="Match mode" className="h-8 w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">Standard formula</SelectItem>
                  <SelectItem value="grouped">By allocation class</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {matchMode === 'grouped' ? (
              <MatchGroupsEditor
                id={`${id}-match-group`}
                groups={matchGroups}
                allocationGroups={allocationGroups}
                memberCounts={matchMemberCounts}
                onAdd={onAddMatchGroup}
                onUpdate={onUpdateMatchGroup}
                onDelete={onDeleteMatchGroup}
              />
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Match tiers</span>
                  <div className="flex gap-2">
                    {onResetTiers && <Button variant="outline" size="sm" className="h-7" onClick={onResetTiers}><RotateCcw className="mr-1 h-3.5 w-3.5" />Restore default tiers</Button>}
                    <Button size="sm" className="h-7" onClick={onAddTier}><Plus className="mr-1 h-3.5 w-3.5" />Add tier</Button>
                  </div>
                </div>
                {matchTiers.map((tier, index) => (
                  <div key={index} className="space-y-1.5 rounded-md border bg-background p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Tier {index + 1}</span>
                      {matchTiers.length > 1 && (
                        <Button aria-label={`Delete match tier ${index + 1}`} variant="ghost" size="sm" onClick={() => onDeleteTier(index)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                      <Slider className="min-w-[16rem] flex-1 space-y-1" validationId={`${id}-tier-${index}-match`} validationLabel={`Match tier ${index + 1} - Match`} label="Match" value={tier.matchPercent} onValueCommit={(val) => onUpdateTier(index, { matchPercent: val[0] })} min={0} max={100} inputMax={400} step={0.25} />
                      <Slider className="min-w-[16rem] flex-1 space-y-1" validationId={`${id}-tier-${index}-up-to`} validationLabel={`Match tier ${index + 1} - Deferrals up to (% of pay)`} label="Deferrals up to (% of pay)" value={tier.upToPercent} onValueCommit={(val) => onUpdateTier(index, { upToPercent: val[0] })} min={0} max={15} inputMax={100} rangeHint step={0.25} />
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">Max possible match</span>
                  <span className="font-medium">{formatPercent(maxPossibleMatch)} of salary</span>
                </div>
              </>
            )}
            {onMatchCapChange && (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <Label className="text-muted-foreground">Maximum match per employee</Label>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">$</span>
                  <NumberInput validationId={`${id}-match-cap`} validationLabel="Match cap per employee" aria-label="Match cap per employee" value={matchCapPerEmployee || ''} onValueChange={onMatchCapChange} emptyValue={0} containerClassName="w-28" className="w-28" min={0} step="100" placeholder="No cap" />
                  <PlanFieldDelta value={matchCapPerEmployee} compareValue={compareTo?.matchCapPerEmployee} show={Boolean(compareTo?.matchEnabled)} format="currency" />
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>}
    </PlanCardShell>
  )
}
